import type { UserModel } from '@/models/UserModel';
import type { ReadReceipt } from '@/types/referral-outbound-center/models/ReadReceipt';
import { ReadReceiptReaderTypeEnum } from '@/types/referral-outbound-center/models/ReadReceipt';

/**
 * Both representations of the signed-in user, because ROC is mid-migration.
 * New messages carry a numeric users.id; ones written before ENG-18289 only
 * carry the Auth0 subject.
 */
export interface CallerIdentity {
  userId: string;
  auth0Sub: string;
}

/**
 * Who is reading a thread. Staff read over a JWT; magic-link recipients are
 * participants and have no users row at all.
 */
export type ThreadReader =
  | { kind: 'user'; caller: CallerIdentity }
  | { kind: 'participant'; participantId: string };

export function toCallerIdentity(
  user: UserModel | undefined
): CallerIdentity | null {
  if (!user) {
    return null;
  }
  return { userId: user.id ?? '', auth0Sub: user.sub ?? '' };
}

export interface MessageAuthorship {
  author_participant_id?: string | null;
  author_user_id?: string | null;
  sender_user_id: string | null;
}

export function isAuthoredByCaller(
  message: MessageAuthorship,
  caller: CallerIdentity | null
): boolean {
  if (!caller) {
    return false;
  }
  if (message.author_user_id) {
    return message.author_user_id === caller.userId;
  }
  // Messages written before ENG-18289 have no author_user_id. sender_user_id
  // holds an Auth0 subject on the API path, but the referral-send producer
  // has always written a numeric users.id there instead — so both have to
  // be checked. The ENG-18290 backfill removes this branch entirely.
  return (
    message.sender_user_id !== null &&
    (message.sender_user_id === caller.auth0Sub ||
      message.sender_user_id === caller.userId)
  );
}

export function isAuthoredByReader(
  message: MessageAuthorship,
  reader: ThreadReader | null
): boolean {
  if (!reader) {
    return false;
  }
  if (reader.kind === 'participant') {
    return message.author_participant_id === reader.participantId;
  }
  return isAuthoredByCaller(message, reader.caller);
}

export function isOwnReceipt(
  receipt: ReadReceipt,
  reader: ThreadReader | null
): boolean {
  if (!reader) {
    return false;
  }
  if (reader.kind === 'participant') {
    return (
      receipt.reader_type === ReadReceiptReaderTypeEnum.Participant &&
      receipt.reader_participant_id === reader.participantId
    );
  }
  if (receipt.reader_type !== ReadReceiptReaderTypeEnum.User) {
    return false;
  }
  if (receipt.reader_user_id) {
    return receipt.reader_user_id === reader.caller.userId;
  }
  // Same pre-backfill fallback as isAuthoredByCaller.
  return receipt.user_id === reader.caller.auth0Sub;
}

/**
 * Which side of the referral wrote a message or holds a read receipt.
 * A referral has one recipient, so this is binary — everyone is either the
 * sender or the recipient. Read receipts are tracked per user, but "has the
 * other side read this" only cares about side, not which specific teammate.
 */
export type ReferralSide = 'sender' | 'participant';

export function messageSide(message: MessageAuthorship): ReferralSide {
  return message.author_participant_id ? 'participant' : 'sender';
}

export interface DocumentEventAuthorship {
  // Absent on the Dynamo read path, which has no such column. Null means
  // the referral sender attached the file.
  uploadedByParticipantId?: string | null;
  // The uploader's facility, not the referral's — documents.ts writes
  // actorContext.uploadedByFacilityId here.
  messageSenderFacilityId: string;
}

/**
 * Which side attached a document.
 *
 * messageSide cannot answer this: a document notice is a system message, so
 * it has no author participant however it was uploaded. The upload itself
 * does, and it rides along on the event payload.
 */
export function documentEventSide(
  event: DocumentEventAuthorship,
  referralSenderFacilityId: string
): ReferralSide {
  if (event.uploadedByParticipantId !== undefined) {
    return event.uploadedByParticipantId ? 'participant' : 'sender';
  }
  return event.messageSenderFacilityId === referralSenderFacilityId
    ? 'sender'
    : 'participant';
}

// A magic-link reader is unambiguous. A staff reader's user_facility_id is
// deprecated on the API contract but still written by read-receipts.ts —
// it's the only way to tell a sender-side staff member from an internal
// recipient's own staff, since reader_user_id alone doesn't carry a side.
export function receiptSide(receipt: ReadReceipt): ReferralSide {
  if (receipt.reader_type === ReadReceiptReaderTypeEnum.Participant) {
    return 'participant';
  }
  // Both fields are Dynamo-shaped facility keys on either read path, so
  // they are comparable. A participant id is not: Postgres reports the
  // real row id there while user_facility_id stays a facility key, and
  // recipient-org staff would file as the sender.
  return receipt.user_facility_id === receipt.recipient_facility_id
    ? 'participant'
    : 'sender';
}
